import { useState, useEffect } from 'react';
import { adminOrdersAPI } from './adminApi';
import s from './AdminOrders.module.css';

const fmt = (n) => `₦${Number(n || 0).toLocaleString()}`;
const STATUSES = ['pending','pending_payment','paid','shipped','delivered','cancelled'];
const STATUS_LABEL = { pending:'Pending payment', pending_payment:'Payment verify', paid:'Paid', shipped:'Waiting to ship', delivered:'Delivered', cancelled:'Cancelled' };
const STATUS_COLOR = { pending:'#F59E0B', pending_payment:'#3B82F6', paid:'#10B981', shipped:'#8B5CF6', delivered:'#10B981', cancelled:'#EF4444' };

export default function AdminOrders() {
  const [orders,   setOrders]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [filter,   setFilter]   = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    adminOrdersAPI.list({ per_page: 100 })
      .then(res => setOrders(res.data.orders || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const updateStatus = async (id, status) => {
    setUpdating(id);
    try {
      await adminOrdersAPI.updateStatus(id, status);
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status, statusLabel: STATUS_LABEL[status] } : o));
    } catch { alert('Failed'); }
    finally { setUpdating(null); }
  };

  const filtered = orders.filter(o => {
    const q = search.toLowerCase();
    return (filter === 'all' || o.status === filter) &&
      (!search || String(o.id).includes(q) || o.address?.name?.toLowerCase().includes(q));
  });

  if (loading) return <div className={s.loading}><div className={s.spin} /></div>;

  return (
    <div className={s.page}>
      <div className={s.header}>
        <h1 className={s.title}>Orders</h1>
        <p className={s.sub}>{orders.length} total orders</p>
      </div>

      <div className={s.toolbar}>
        <input className={s.search} placeholder="Search order ID or name…" value={search} onChange={e => setSearch(e.target.value)} />
        <div className={s.filters}>
          {['all', ...STATUSES].map(f => (
            <button key={f} className={`${s.filter} ${filter === f ? s.filterActive : ''}`} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : STATUS_LABEL[f]}
            </button>
          ))}
        </div>
      </div>

      <div className={s.list}>
        {filtered.length === 0 && <p className={s.empty}>No orders found</p>}
        {filtered.map(o => (
          <div key={o.id} className={s.card}>
            <div className={s.row} onClick={() => setExpanded(expanded === o.id ? null : o.id)}>
              <div className={s.oid}><span className={s.ohash}>#</span>{o.id}</div>
              <div><p className={s.oname}>{o.address?.name || '—'}</p><p className={s.oloc}>{o.address?.city}, {o.address?.state}</p></div>
              <p className={s.ototal}>{fmt(o.total)}</p>
              <span className={s.badge} style={{ color: STATUS_COLOR[o.status], background: STATUS_COLOR[o.status] + '18' }}>{STATUS_LABEL[o.status] || o.status}</span>
              <p className={s.odate}>{new Date(o.createdAt).toLocaleDateString()}</p>
              <span className={s.chev}>{expanded === o.id ? '▲' : '▼'}</span>
            </div>

            {expanded === o.id && (
              <div className={s.detail}>
                <div className={s.detailGrid}>
                  <div className={s.sec}>
                    <p className={s.secTitle}>Items</p>
                    {o.items?.map((item, i) => (
                      <div key={i} className={s.item}>
                        <span className={s.iname}>{item.name || `Product #${item.productId}`}</span>
                        <span className={s.iqty}>×{item.quantity}</span>
                        <span className={s.iprice}>{fmt(item.unitPrice * item.quantity)}</span>
                      </div>
                    ))}
                    <div className={s.itotal}><span>Delivery</span><span>{fmt(o.deliveryFee)}</span></div>
                    <div className={`${s.itotal} ${s.igrand}`}><span>Total</span><span>{fmt(o.total)}</span></div>
                  </div>

                  <div className={s.sec}>
                    <p className={s.secTitle}>Delivery address</p>
                    <p className={s.addrLine}>{o.address?.name}</p>
                    <p className={s.addrLine}>{o.address?.line}</p>
                    <p className={s.addrLine}>{o.address?.city}, {o.address?.state}</p>
                    <p className={s.addrLine}>📞 {o.address?.phone}</p>
                  </div>

                  <div className={s.sec}>
                    <p className={s.secTitle}>Update status</p>
                    <div className={s.statusBtns}>
                      {STATUSES.map(st => (
                        <button
                          key={st}
                          className={`${s.statusBtn} ${o.status === st ? s.statusBtnActive : ''}`}
                          style={o.status === st ? { background: STATUS_COLOR[st] + '18', color: STATUS_COLOR[st], borderColor: STATUS_COLOR[st] } : {}}
                          onClick={() => updateStatus(o.id, st)}
                          disabled={updating === o.id}
                        >
                          {STATUS_LABEL[st]}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
